import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Image, ExternalLink, AlertTriangle, ArrowRight } from "lucide-react";
import type { PageIssue, NonNextgenImageDetails } from "@/types/technicalAudit";

interface NonNextgenImageCardProps {
	issue: PageIssue;
}

export function NonNextgenImageCard({ issue }: NonNextgenImageCardProps) {
	const details = issue.issue_details as NonNextgenImageDetails;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Non-Next-Gen Image Format</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						This image uses the <strong>{details.current_format.toUpperCase()}</strong> format. Modern formats like WebP or AVIF provide better compression without quality loss.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Image className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Format Conversion
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<div className="flex items-center justify-center gap-3 py-2">
						<Badge variant="outline" className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 text-sm px-3 py-1">
							{details.current_format.toUpperCase()}
						</Badge>
						<ArrowRight className="w-5 h-5 text-muted-foreground" />
						<Badge variant="outline" className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 text-sm px-3 py-1">
							{details.recommended_format.toUpperCase()}
						</Badge>
					</div>
					<a
						href={details.image_url}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
					>
						<p className="text-sm font-mono break-all text-foreground flex-1">
							{details.image_url}
						</p>
						<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
					</a>
				</CardContent>
			</Card>
		</div>
	);
}
